import { Link } from "react-router-dom";
import { SITE } from "@/lib/siteInfo";

// Shared brand nav + footer for public (no-login) pages: Pricing, Terms, Refunds.
export default function PublicShell({ children, testId = "public-page" }) {
  const year = new Date().getFullYear();
  return (
    <div className="min-h-screen flex flex-col bg-riq-bg text-slate-900" data-testid={testId}>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/50 shadow-sm">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-nav-logo">
            <img src="/sliply-mark.png" alt="Sliply" className="w-9 h-9 p-1.5 rounded-xl bg-riq-primary" />
            <div className="leading-none">
              <div className="font-head font-black text-lg tracking-tight">Sliply</div>
              <div className="text-[10px] text-riq-primary/70 tracking-[0.18em] uppercase">{SITE.domain}</div>
            </div>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-3">
            <Link to="/hpi" data-testid="public-nav-hpi" className="whitespace-nowrap text-xs sm:text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors">Price Index</Link>
            <Link to="/pricing" data-testid="public-nav-pricing" className="text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors">Pricing</Link>
            <Link to="/login" data-testid="public-nav-login" className="whitespace-nowrap text-xs sm:text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors">Log in</Link>
            <Link to="/register" data-testid="public-nav-signup" className="inline-flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 active:scale-95 transition-transform">
              Sign up free
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 w-full">{children}</main>

      <PublicFooter year={year} />
    </div>
  );
}

export function PublicFooter({ year }) {
  return (
    <footer className="bg-slate-900 text-white px-6 pt-14 pb-10" data-testid="public-footer">
      <div className="max-w-6xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/sliply-mark.png" alt="Sliply" className="w-8 h-8" />
            <div>
              <div className="font-head font-bold leading-none">Sliply</div>
              <div className="text-[11px] text-slate-400 tracking-[0.15em] uppercase">{SITE.domain}</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xs">
            Turn every till slip into a searchable spending brain — for homes and businesses.
          </p>
        </div>

        <FooterCol title="Product">
          <FooterLink to="/pricing" testId="footer-link-pricing">Pricing</FooterLink>
          <FooterLink to="/hpi" testId="footer-link-hpi">Household Price Index</FooterLink>
          <FooterLink to="/register" testId="footer-link-signup">Sign up</FooterLink>
          <FooterLink to="/login" testId="footer-link-login">Log in</FooterLink>
        </FooterCol>

        <FooterCol title="Legal">
          <FooterLink to="/terms" testId="footer-link-terms">Terms of Service</FooterLink>
          <FooterLink to="/refunds" testId="footer-link-refunds">Refund &amp; Cancellation</FooterLink>
        </FooterCol>

        <FooterCol title="Contact">
          <a href={`mailto:${SITE.email}`} data-testid="footer-contact-email" className="block text-sm text-slate-300 hover:text-white transition-colors">{SITE.email}</a>
          {SITE.phone ? <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="footer-contact-phone" className="block text-sm text-slate-300 hover:text-white transition-colors mt-2">{SITE.phone}</a> : null}
          {SITE.address ? <div data-testid="footer-contact-address" className="text-sm text-slate-400 mt-2 whitespace-pre-line">{SITE.address}</div> : null}
        </FooterCol>
      </div>

      <div className="max-w-6xl mx-auto mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
        <span>© {year} {SITE.legalName}. All rights reserved.</span>
        <div className="flex items-center gap-5">
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/refunds" className="hover:text-white transition-colors">Refunds</Link>
          <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
      <div className="mt-4 space-y-2">{children}</div>
    </div>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <Link to={to} data-testid={testId} className="block text-sm text-slate-300 hover:text-white transition-colors">{children}</Link>
  );
}

// Reusable legal-page scaffold (title + prose sections).
export function LegalPage({ title, subtitle, updated, children, testId }) {
  return (
    <section className="px-6 py-14 sm:py-20" data-testid={testId}>
      <div className="max-w-3xl mx-auto">
        <h1 className="font-head font-black tracking-tighter text-4xl sm:text-5xl">{title}</h1>
        {subtitle ? <p className="mt-4 text-lg text-slate-600 leading-relaxed">{subtitle}</p> : null}
        {updated ? <p className="mt-3 text-sm text-slate-400">Last updated: {updated}</p> : null}
        <div className="mt-10 space-y-8">{children}</div>
      </div>
    </section>
  );
}

export function LegalSection({ n, title, children }) {
  return (
    <div data-testid={`legal-section-${n}`}>
      <h2 className="font-head text-xl sm:text-2xl font-bold tracking-tight flex items-baseline gap-3">
        <span className="text-riq-primary/60 text-base font-black">{n}</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-slate-600">{children}</div>
    </div>
  );
}
